import React, {useContext, useState} from 'react'
import {ParsedQuery} from 'query-string'
import {FormattedMessage} from 'react-intl'
import {booleanFromQuery, booleanToQuery} from '../../../helpers/coreUtil'
import {IValuesToQuery, useQuery, useQueryObj} from '../../hocs/hooks'
import {Form} from 'react-final-form'
import AngleRight from '../../../assets/icons/AngleRight.svg'
import Times from '../../../assets/icons/Times.svg'
import {freeFacilityList, nearbyFacilityList, nonFreeFacilityList} from '../../../config/constants'
import {RouterContext} from '../../containers/Context'
import BackDrop from '../../containers/BackDrop'
import searchStyles from '../cars/CoverPC.scss'
import styles from './StationsSearch.scss'
import CheckField from '../../fields/custom/CheckField'
import { freeFacilityListMsg, nearbyFacilityListMsg, nonFreeFacilityListMsg } from '../../../config/commonMessages'

export const queryToValues = (
  {
    ...query
  }: ParsedQuery<string>) => ({
  ...query,
  ...Object.fromEntries([
    ...freeFacilityList,
    ...nonFreeFacilityList,
    ...nearbyFacilityList,
  ].map(fac => [fac, booleanFromQuery(query[fac])])),
})

export const valuesToQuery: IValuesToQuery<typeof queryToValues> = (
  {
    ...query
  }) => ({
  ...query,
  ...Object.fromEntries([
    ...freeFacilityList,
    ...nonFreeFacilityList,
    ...nearbyFacilityList,
  ].map(fac => [fac, booleanToQuery(query[fac])]))
})

const StationsSearch = ({onClose}: {
    onClose: () => any
}) => {
  const {history} = useContext(RouterContext)!
  const [isOpenFree, setIsOpenFree] = useState(false)
  const [isOpenNonFree, setIsOpenNonFree] = useState(false)
  const [isOpenNearbyFacility, setIsOpenNearbyFacility] = useState(false)
  const initialValues = useQuery(queryToValues)
  const queryObj = useQueryObj(queryToValues, valuesToQuery)
  const [values, setValues] = useState(initialValues)
  const onCarSubmit = (newValues: any) => {
    if (newValues.page) newValues.page = undefined
    history.push(queryObj({
      ...newValues,
      ...Object.fromEntries([
        ...nearbyFacilityList.map(key => [key, newValues[key]]),
        ...freeFacilityList.map(key => [key, newValues[key]]),
        ...nonFreeFacilityList.map(key => [key, newValues[key]]),
      ])
    }))
  }
  return <Form onSubmit={onCarSubmit} initialValues={values}>
    {({handleSubmit, form}) => {
      const resetSearch = () => {
        setValues({})
        form.reset()
      }
      return <>
      <form onSubmit={handleSubmit}
            className={searchStyles.searchBox}
            id="refineSearch">
        <BackDrop onBackgroundClicked={onClose}>
          <div tabIndex={-1} role="dialog" className={['m-2 modal-dialog-scrollable', styles.modalPanel].join(' ')}>
            <div className="modal-content w-100">
              <div className="modal-header py-4">
                <div className="w-100 text-center">
                  <h3 className={['m-0 font-weight-medium'].join(' ')}>
                    <FormattedMessage id="stationsSearch.title"/>
                  </h3>
                </div>
                <Times className={styles.cross} onClick={onClose}/>
              </div>

              <div className="modal-body m-3">
                <div className="form-group pb-md-3">
                  <div className="font-weight-bold mb-2">
                    <span
                      className="c-pointer"
                      onClick={() => setIsOpenFree(!isOpenFree)}>
                      <AngleRight className={['mb-1 mr-2 fa font-size-75', isOpenFree && styles.rotate].filter(Boolean).join(' ')}/>
                      <FormattedMessage id="station.freeFacility"/>
                    </span>
                  </div>
                  {isOpenFree
                    && <>
                    {[...Array(Math.ceil(freeFacilityList.length / 3)).keys()]
                      .map(index => (<div className="row mb-md-1" key={index}>
                        {freeFacilityList.slice(index * 3, index * 3 + 3)
                          .map(facility => <div className="col-md-4 mb-2" key={facility}>
                            <CheckField name={facility}>
                              <FormattedMessage {...freeFacilityListMsg[facility]}/>
                            </CheckField>
                          </div>)}
                      </div>))}
                  </>}
                </div>
                <div className="form-group">
                  <div className="font-weight-bold pb-md-3">
                    <span
                      className="c-pointer"
                      onClick={() => setIsOpenNonFree(!isOpenNonFree)}
                    >
                      <AngleRight className={['mb-1 mr-2 fa font-size-75', isOpenNonFree && styles.rotate].filter(Boolean).join(' ')}/>
                      <FormattedMessage id="station.nonFreeFacility"/>
                    </span>
                  </div>
                  {isOpenNonFree
                    && <>
                    {[...Array(Math.ceil(nonFreeFacilityList.length / 3)).keys()]
                      .map(index => (<div className="row mb-md-1" key={index}>
                      {nonFreeFacilityList.slice(index * 3, index * 3 + 3)
                        .map(facility => <div className="col-md-4 mb-2" key={facility}>
                          <CheckField name={facility}>
                            <FormattedMessage {...nonFreeFacilityListMsg[facility]}/>
                          </CheckField>
                        </div>)}
                    </div>))}
                  </>}
                </div>
                <div className="form-group">
                  <div className="font-weight-bold mb-2">
                    <span
                      className="c-pointer"
                      onClick={() => setIsOpenNearbyFacility(!isOpenNearbyFacility)}
                    >
                      <AngleRight className={['mb-1 mr-2 fa font-size-75', isOpenNearbyFacility && styles.rotate].filter(Boolean).join(' ')}/>
                      <FormattedMessage id="station.nearbyFacility"/>
                    </span>
                  </div>
                  {isOpenNearbyFacility
                    && <>
                    {[...Array(Math.ceil(nearbyFacilityList.length / 3)).keys()]
                      .map(index => (<div className="row mb-md-1" key={index}>
                      {nearbyFacilityList.slice(index * 3, index * 3 + 3)
                        .map(facility => <div className="col-md-4 mb-2" key={facility}>
                          <CheckField name={facility}>
                            <FormattedMessage {...nearbyFacilityListMsg[facility]}/>
                          </CheckField>
                        </div>)}
                    </div>))}
                  </>}
                </div>
              </div>
              <div className="modal-footer py-4">
                <div className="w-100 text-center d-flex flex-column align-items-center">
                  <button onClick={onClose} className={['btn px-4 py-0 mb-2 font-weight-bold btn-primary', styles.button].join(' ')} type="submit">
                    <FormattedMessage id="carsSearch.close" />
                  </button>
                  <div
                    onClick={resetSearch}
                    className={
                      [
                        'c-pointer d-flex btn align-items-center px-4 py-0 font-weight-bold btn-outline-primary',
                        styles.button
                      ].join(' ')}
                  >
                    <div className="text-center w-100">
                      <FormattedMessage id="carsSearch.clear"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BackDrop>
      </form>
    </>
    }}
  </Form>
}
StationsSearch.displayName = 'StationsSearch'
export default StationsSearch
